import type { AssetRef } from "@loumy/contracts";
import { create } from "zustand";

// UI-State (Client-only): Theme, ausgewählter Ordner (Inbox = null), je-Tool-Draft der
// Settings UND die je-Tool/Rolle hochgeladenen Input-Assets (D61). Server-State liegt
// getrennt in TanStack Query (state/queries).

type Theme = "dark" | "light";

/** Ein hochgeladenes Input-Asset + optionale lokale Preview-URL (Object-URL der Datei). `asset` ist
 * bewusst die schmale `AssetRef` (id/kind/r2_key) — genau das, was Konsumenten brauchen (Dropzone-
 * Key/Remove, Submit-`assetId`, Credits-`inputRefs`); ein volles `AssetRow` ist überspezifiziert.
 * AssetRow-Quellen (Upload, Job-Rehydrierung) sind strukturell zuweisbar; rehydrierte/force-Inputs
 * (nur AssetRef vorhanden, z.B. `preparedAsset` im spritesheet-Force-Retry) passen direkt. */
export interface ToolInput {
  asset: AssetRef;
  previewUrl?: string;
}

interface UiState {
  theme: Theme;
  toggleTheme: () => void;

  authOpen: boolean; // Login/Sign-up-Popup (Identität, D47)
  openAuth: () => void;
  closeAuth: () => void;

  selectedFolderId: string | null; // null = Inbox-View (folder_id IS NULL, §11/D20)
  selectFolder: (id: string | null) => void;

  // Geöffneter Job (§11/D65) — beim „Job öffnen" gesetzt; Studio lädt das Detail (useJobDetail)
  // und rehydriert Settings/Inputs/Output. null = kein Job offen (frischer Compose-Zustand).
  openedJobId: string | null;
  openJob: (jobId: string) => void;
  closeJob: () => void;

  drafts: Record<string, Record<string, unknown>>;
  getDraft: (toolId: string) => Record<string, unknown> | undefined;
  setDraft: (toolId: string, value: Record<string, unknown>) => void;

  // Hochgeladene Input-Assets je Tool und Rolle (D61) — Brücke MediaPanel → SettingsPanel-Submit.
  // `max` (optional) = hartes Obergrenzen-Gate je Rolle: ein paralleler Drop/Upload-Race darf den
  // Slot nicht über `core.inputs[].max` hinaus füllen (sonst akzeptiert die Vorschau Inputs, die der
  // Server fail-closed ablehnt). Ohne `max` (z.B. Job-Rehydrierung) gilt keine Grenze.
  inputs: Record<string, Record<string, ToolInput[]>>;
  addInput: (toolId: string, role: string, item: ToolInput, max?: number) => void;
  removeInput: (toolId: string, role: string, assetId: string) => void;
  clearInputs: (toolId: string) => void;

  // Monoton steigender Zähler, der bei JEDEM resetUserScopedState (Logout/Identitätswechsel)
  // inkrementiert wird (D65/Codex-P1). Async-Continuations (inflight-Upload) erfassen ihn beim
  // Start und verwerfen ihr Ergebnis, wenn er sich bis zur Completion geändert hat — sonst
  // schreibt ein während des Logouts laufender Upload alten User-State zurück.
  sessionEpoch: number;

  // User-gebundenen Client-State leeren (D47/D65): bei Logout/Identitätswechsel — sonst sähe der
  // nächste User im selben Browser fremde Drafts, Input-Previews (presigned URLs!) und den
  // geöffneten Job. Theme bleibt (Geräte-Präferenz, nicht user-scoped). Ergänzt qc.clear() (session).
  resetUserScopedState: () => void;
}

const THEME_KEY = "loumy.theme";
const initialTheme: Theme =
  (typeof localStorage !== "undefined" && (localStorage.getItem(THEME_KEY) as Theme | null)) || "dark";

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialTheme,
  toggleTheme: () =>
    set((s) => {
      const theme: Theme = s.theme === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_KEY, theme);
      } catch {
        /* private mode */
      }
      return { theme };
    }),

  authOpen: false,
  openAuth: () => set({ authOpen: true }),
  closeAuth: () => set({ authOpen: false }),

  selectedFolderId: null,
  selectFolder: (id) => set({ selectedFolderId: id }),

  openedJobId: null,
  openJob: (jobId) => set({ openedJobId: jobId }),
  closeJob: () => set({ openedJobId: null }),

  sessionEpoch: 0,

  drafts: {},
  getDraft: (toolId) => get().drafts[toolId],
  setDraft: (toolId, value) => set((s) => ({ drafts: { ...s.drafts, [toolId]: value } })),

  inputs: {},
  addInput: (toolId, role, item, max) =>
    set((s) => {
      const forTool = s.inputs[toolId] ?? {};
      const forRole = forTool[role] ?? [];
      // Ablehnen, wenn (a) dasselbe Asset bereits vorliegt (idempotent, Doppel-Upload/Drop-Race)
      // ODER (b) der Slot voll ist (hartes `max`-Gate gegen parallelen Upload-Race, Codex-P1) →
      // die evtl. mitgegebene LOKALE blob-Preview freigeben (presigned-https = revoke-no-op), sonst
      // verwaist sie (das abgelehnte Item kommt nie in den Store, der removeInput/clear-Pfad kennt es nie).
      const duplicate = forRole.some((i) => i.asset.id === item.asset.id);
      const overflow = max !== undefined && forRole.length >= max;
      if (duplicate || overflow) {
        if (item.previewUrl) {
          try {
            URL.revokeObjectURL(item.previewUrl);
          } catch {
            /* no-op */
          }
        }
        return s;
      }
      return { inputs: { ...s.inputs, [toolId]: { ...forTool, [role]: [...forRole, item] } } };
    }),
  removeInput: (toolId, role, assetId) =>
    set((s) => {
      const forTool = s.inputs[toolId];
      const forRole = forTool?.[role];
      if (!forTool || !forRole) return s;
      const removed = forRole.find((i) => i.asset.id === assetId);
      if (removed?.previewUrl) {
        try {
          URL.revokeObjectURL(removed.previewUrl);
        } catch {
          /* no-op */
        }
      }
      return {
        inputs: { ...s.inputs, [toolId]: { ...forTool, [role]: forRole.filter((i) => i.asset.id !== assetId) } },
      };
    }),
  clearInputs: (toolId) =>
    set((s) => {
      for (const items of Object.values(s.inputs[toolId] ?? {})) {
        for (const i of items) {
          if (i.previewUrl) {
            try {
              URL.revokeObjectURL(i.previewUrl);
            } catch {
              /* no-op */
            }
          }
        }
      }
      const next = { ...s.inputs };
      delete next[toolId];
      return { inputs: next };
    }),

  resetUserScopedState: () =>
    set((s) => {
      // Object-URLs der Inputs freigeben (kein Leak); presigned https-Previews sind hier No-op.
      for (const byRole of Object.values(s.inputs)) {
        for (const items of Object.values(byRole)) {
          for (const i of items) {
            if (i.previewUrl) {
              try {
                URL.revokeObjectURL(i.previewUrl);
              } catch {
                /* no-op */
              }
            }
          }
        }
      }
      // sessionEpoch hochzählen → inflight-Uploads erkennen den Identitätswechsel und verwerfen.
      return { inputs: {}, drafts: {}, openedJobId: null, selectedFolderId: null, sessionEpoch: s.sessionEpoch + 1 };
    }),
}));
